// Персистентность очереди исходящих (порт OutboxStore). SPEC client-transport §4.6.
//
// Дефолт — IndexedDB (переживает закрытие вкладки/перезагрузку — инвариант 2). Отдельная БД от
// офлайн-кеша client-store (TOPOLOGY: транспорт владеет своей очередью). Один object-store `outbox`
// с keyPath `clientMsgId`. В тестах подменяется fake-indexeddb (глобальный indexedDB) или
// in-memory store. Форма записи — OutboxItem (ports.ts), сериализуется структурно (structured clone).
const DB_NAME = 'tele-chat-client-transport-outbox';
const STORE_NAME = 'outbox';
const DB_VERSION = 1;
const KEY_PATH = 'clientMsgId';
function reqAsPromise(req) {
    return new Promise((resolve, reject) => {
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
    });
}
function openDb(factory) {
    return new Promise((resolve, reject) => {
        const req = factory.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = () => {
            const db = req.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME, { keyPath: KEY_PATH });
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB open failed'));
    });
}
/**
 * IndexedDB-реализация OutboxStore. `factory` инъецируется (дефолт — глобальный `indexedDB`); в
 * тестах сюда уходит fake-indexeddb. Бросает явную ошибку, если IndexedDB недоступен и factory
 * не передан (тогда зовущий должен дать createMemoryOutboxStore()).
 */
export function createIndexedDbOutboxStore(factory) {
    const idb = factory ?? (typeof indexedDB !== 'undefined' ? indexedDB : undefined);
    if (!idb) {
        throw new Error('IndexedDB недоступен в этой среде. Передайте factory (например, fake-indexeddb) ' +
            'или используйте createMemoryOutboxStore() для тестов/SSR.');
    }
    let dbPromise = null;
    function db() {
        if (!dbPromise)
            dbPromise = openDb(idb);
        return dbPromise;
    }
    function commit(tx) {
        return new Promise((resolve, reject) => {
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error ?? new Error('IndexedDB tx failed'));
            tx.onabort = () => reject(tx.error ?? new Error('IndexedDB tx aborted'));
        });
    }
    return {
        async put(item) {
            const database = await db();
            const tx = database.transaction(STORE_NAME, 'readwrite');
            tx.objectStore(STORE_NAME).put(item);
            await commit(tx);
        },
        async getAll() {
            const database = await db();
            const tx = database.transaction(STORE_NAME, 'readonly');
            const all = await reqAsPromise(tx.objectStore(STORE_NAME).getAll());
            return all ?? [];
        },
        async delete(clientMsgId) {
            const database = await db();
            const tx = database.transaction(STORE_NAME, 'readwrite');
            tx.objectStore(STORE_NAME).delete(clientMsgId);
            await commit(tx);
        },
    };
}
/**
 * In-memory OutboxStore — для тестов/SSR без IndexedDB. Клонирует записи (structuredClone), чтобы
 * имитировать сериализацию IndexedDB (никаких живых ссылок между очередью и store).
 */
export function createMemoryOutboxStore() {
    const map = new Map();
    const clone = (i) => typeof structuredClone === 'function'
        ? structuredClone(i)
        : JSON.parse(JSON.stringify(i));
    return {
        async put(item) {
            map.set(item.clientMsgId, clone(item));
        },
        async getAll() {
            return [...map.values()].map(clone);
        },
        async delete(clientMsgId) {
            map.delete(clientMsgId);
        },
    };
}
//# sourceMappingURL=outbox-store.js.map